import { Chip, IconButton, LinearProgress, Tooltip } from '@mui/material'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SyncIcon from '@mui/icons-material/Sync'
import { useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  EMR_SYNC_STORE_META,
  ensurePeriodicEmrBackgroundSync,
  pauseEmrSyncStore,
  resetEmrStoreAndResync,
  resumeEmrSyncStore,
  startEmrBackgroundSync,
  subscribeEmrSyncState,
  syncSingleEmrStore,
} from 'src/emr/database/emrBackgroundSync'
import { countRecordsInStore } from 'src/emr/database/indexedDbDao'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'

// visual meta per engine status
const STATUS_META = {
  syncing: { label: 'Syncing…', color: '#1d4ed8', bg: 'rgba(29, 78, 216, 0.08)' },
  synced: { label: 'Synced', color: '#15803d', bg: 'rgba(21, 128, 61, 0.08)' },
  fresh: { label: 'Up to date', color: '#15803d', bg: 'rgba(21, 128, 61, 0.08)' },
  paused: { label: 'Paused', color: '#b45309', bg: 'rgba(180, 83, 9, 0.1)' },
  interrupted: { label: 'Resumable', color: '#b45309', bg: 'rgba(180, 83, 9, 0.1)' },
  failed: { label: 'Failed — will retry', color: '#b91c1c', bg: 'rgba(185, 28, 28, 0.08)' },
  resetting: { label: 'Resetting…', color: '#1d4ed8', bg: 'rgba(29, 78, 216, 0.08)' },
  'not-applicable': { label: 'Not applicable', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.08)' },
  idle: { label: 'Idle', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.08)' },
}

function rowPercent(state) {
  if (!state) return 0
  if (state.status === 'synced' || state.status === 'fresh') return 100
  if (typeof state.percent === 'number') return Math.round(state.percent)
  return state.lastSyncedAt ? 100 : 0
}

function formatLastSynced(value) {
  if (!value) return 'never'
  return `${value} UTC`
}

/** One store row: progress bar, done/pending detail, per-table controls. */
function SyncStoreRow({ progressKey, state, localCount, busyGlobal, onAction }) {
  const meta = EMR_SYNC_STORE_META[progressKey]
  const status = state?.status || 'idle'
  const statusMeta = STATUS_META[status] || STATUS_META.idle
  const percent = rowPercent(state)
  const isRunning = status === 'syncing' || status === 'resetting'
  const resumeCursor = state?.resumeCursors?.[0]?.cursor

  // completed vs pending, phrased from the best information we have
  let detail = ''
  if (isRunning && state?.page) {
    const total = state.totalPages || '?'
    detail = `page ${state.page} of ~${total} · ${state.records || 0} records this run`
  } else if (status === 'interrupted' && resumeCursor) {
    const done = (resumeCursor.nextPage || 1) - 1
    detail = `${done} page(s) downloaded (${resumeCursor.records || 0} records) — resumes at page ${resumeCursor.nextPage}`
  } else if (status === 'failed' && state?.error) {
    detail = state.error
  } else if (status === 'paused' && resumeCursor) {
    detail = `paused at page ${(resumeCursor.nextPage || 1) - 1} — resume continues from there`
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(160px, 1.2fr) 2fr auto',
        gap: '12px',
        alignItems: 'center',
        padding: '10px 12px',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{meta?.label || progressKey}</div>
        <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
          {localCount != null ? `${localCount.toLocaleString()} records locally · ` : ''}
          last synced {formatLastSynced(state?.lastSyncedAt)}
        </div>
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
          <Chip
            size="small"
            label={statusMeta.label}
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 700,
              color: statusMeta.color,
              backgroundColor: statusMeta.bg,
            }}
          />
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: statusMeta.color }}>
            {percent}%
          </span>
          {state?.clinicBrandId && isRunning && (
            <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>({state.clinicBrandId})</span>
          )}
        </div>
        <LinearProgress
          variant={isRunning && !state?.page ? 'indeterminate' : 'determinate'}
          value={percent}
          sx={{
            height: 6,
            borderRadius: 3,
            '& .MuiLinearProgress-bar': { backgroundColor: statusMeta.color },
            backgroundColor: 'rgba(0,0,0,0.08)',
          }}
        />
        {detail && (
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '3px' }}>{detail}</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
        <Tooltip title={`Update ${meta?.label || progressKey} now`}>
          <span>
            <IconButton
              size="small"
              disabled={isRunning || busyGlobal}
              onClick={() => onAction('update', progressKey)}
            >
              <SyncIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={state?.paused ? 'Resume syncing this table' : 'Pause syncing this table'}>
          <span>
            <IconButton size="small" onClick={() => onAction(state?.paused ? 'resume' : 'pause', progressKey)}>
              {state?.paused ? (
                <PlayCircleOutlineIcon fontSize="small" />
              ) : (
                <PauseCircleOutlineIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={`Reset ${meta?.label || progressKey}: clear local copy and re-download`}>
          <span>
            <IconButton
              size="small"
              disabled={isRunning || busyGlobal}
              onClick={() => onAction('reset', progressKey)}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  )
}

// EMR local-data sync. The engine lives in src/emr/database/emrBackgroundSync.js
// (single-flight, cross-tab locked, page-cursor resume, per-store pause); this
// component either starts the automatic periodic sync (takeActionsItself) or renders
// the granular per-table panel with progress bars and Update / Pause / Reset controls.
export const ERMOptionsSyncComponent = ({ takeActionsItself, mentorClinicAssociationData }) => {
  const [storeStates, setStoreStates] = useState({})
  const [localCounts, setLocalCounts] = useState({})
  const [isSyncingAll, setIsSyncingAll] = useState(false)

  // The auto-sync instance is mounted without props (MentorHeaderAndContent), so
  // fall back to the profile context — without this the background population has
  // no clinic brands to sync and silently does nothing.
  const profileContext = useContext(MentorProfileContext)
  const associationData =
    mentorClinicAssociationData || profileContext?.mentorClinicAssociationData

  const clinicBrandUniqueIds = useMemo(
    () => [...new Set((associationData || []).map((item) => item?.clinicBrandId).filter(Boolean))],
    [associationData],
  )

  // live engine state for every table (also carries resume cursors + pause flags)
  useEffect(() => {
    if (takeActionsItself) return undefined
    return subscribeEmrSyncState(setStoreStates)
  }, [takeActionsItself])

  // local record counts per table — the truthful "how much is complete" number
  useEffect(() => {
    if (takeActionsItself) return undefined
    let cancelled = false
    async function refreshCounts() {
      const counts = {}
      for (const [progressKey, meta] of Object.entries(EMR_SYNC_STORE_META)) {
        let total = 0
        for (const storeName of meta.idbStoreNames) {
          try {
            total += await countRecordsInStore(storeName)
          } catch (error) {
            // store may not exist yet (pre-upgrade) — count stays partial
          }
        }
        counts[progressKey] = total
      }
      if (!cancelled) setLocalCounts(counts)
    }
    refreshCounts()
    const interval = setInterval(refreshCounts, 15 * 1000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [takeActionsItself])

  // Automatic background population: starts once the browser is idle, re-syncs
  // periodically, never runs twice (single-flight per tab + Web Locks across tabs).
  const clinicBrandIdsKey = clinicBrandUniqueIds.join(',')
  useEffect(() => {
    if (takeActionsItself && clinicBrandUniqueIds.length > 0) {
      ensurePeriodicEmrBackgroundSync({ clinicBrandIds: clinicBrandUniqueIds })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeActionsItself, clinicBrandIdsKey])

  if (takeActionsItself) {
    return null
  }

  async function handleUpdateAll() {
    setIsSyncingAll(true)
    try {
      const result = await startEmrBackgroundSync({
        clinicBrandIds: clinicBrandUniqueIds,
        force: true,
        waitForIdleFirst: false,
      })
      if (result?.failures?.length > 0) {
        toast.error(
          `Sync finished with errors in: ${result.failures.map((item) => item.store).join(', ')}`,
        )
      } else if (result?.skippedRun) {
        toast.info('A sync is already running in another tab')
      } else {
        toast.success('Data sync completed')
      }
    } catch (error) {
      console.error('Sync failed', error)
      toast.error('Data sync failed — please try again')
    } finally {
      setIsSyncingAll(false)
    }
  }

  async function handleRowAction(action, progressKey) {
    const label = EMR_SYNC_STORE_META[progressKey]?.label || progressKey
    try {
      if (action === 'pause') {
        pauseEmrSyncStore(progressKey)
        toast.info(`${label} sync paused — progress is kept and resumes exactly where it stopped`)
      } else if (action === 'resume') {
        resumeEmrSyncStore(progressKey)
        toast.info(`${label} sync resumed`)
        await syncSingleEmrStore({ progressKey, clinicBrandIds: clinicBrandUniqueIds, force: false })
      } else if (action === 'update') {
        await syncSingleEmrStore({ progressKey, clinicBrandIds: clinicBrandUniqueIds, force: true })
        toast.success(`${label} updated`)
      } else if (action === 'reset') {
        if (
          !window.confirm(
            `Reset ${label}? The local copy is cleared and re-downloaded from the server.`,
          )
        ) {
          return
        }
        await resetEmrStoreAndResync({ progressKey, clinicBrandIds: clinicBrandUniqueIds })
        toast.success(`${label} reset and re-downloaded`)
      }
    } catch (error) {
      console.error(`Sync action ${action} failed for ${progressKey}`, error)
      toast.error(`${label}: ${action} failed — please try again`)
    }
  }

  const storeKeys = Object.keys(EMR_SYNC_STORE_META)
  const syncedCount = storeKeys.filter((key) => {
    const status = storeStates[key]?.status
    return status === 'synced' || status === 'fresh' || (!status && storeStates[key]?.lastSyncedAt)
  }).length
  const pendingKeys = storeKeys.filter((key) =>
    ['interrupted', 'failed', 'syncing', 'resetting'].includes(storeStates[key]?.status),
  )

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '10px',
        }}
      >
        <div>
          <b>Update Local Data (Drugs/Diagnosis/etc.)</b>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            {syncedCount} of {storeKeys.length} tables up to date
            {pendingKeys.length > 0 &&
              ` · pending: ${pendingKeys
                .map((key) => EMR_SYNC_STORE_META[key]?.label || key)
                .join(', ')}`}
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleUpdateAll} disabled={isSyncingAll}>
          {isSyncingAll ? 'Updating…' : 'Update All Now'}
        </button>
      </div>

      <div
        style={{
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: '12px',
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        {storeKeys.map((progressKey) => (
          <SyncStoreRow
            key={progressKey}
            progressKey={progressKey}
            state={storeStates[progressKey]}
            localCount={localCounts[progressKey]}
            busyGlobal={isSyncingAll}
            onAction={handleRowAction}
          />
        ))}
      </div>
      <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '6px' }}>
        Interrupted downloads resume from the last completed page — closing the tab or pausing
        never loses progress. Paused tables are skipped by the automatic background sync until
        resumed.
      </div>
    </div>
  )
}
